import { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { clearListCache } from "../../utils/listCache"
import { changeAffiliateStatus, checkIsAffiliateContactGroupOwner } from "../../features/affiliates/affiliates"

const IDLE = { pending: false, isOwner: false, targetStatus: '', targetUserId: '' }

// Shared confirm-and-dispatch logic for both affiliate status controls
// (EditAffiliates dropdown and the AffiliatesTable inline badge action).
// A single `busy` flag guards the whole flow — owner pre-check, open confirm,
// PATCH — so a second change cannot start until the current one settles or is cancelled.
const useAffiliateStatusChange = ({ onSuccess } = {}) => {
  const dispatch = useDispatch()
  const [state, setState] = useState(IDLE)
  const [busy, setBusy] = useState(false)
  // ConfirmModal closes itself (setOpen(false)) right after functionButton(); that close
  // path must not be treated as a cancel while the PATCH is still in flight.
  const inFlightRef = useRef(false)

  const requestChange = async ({ userId, status, currentStatus }) => {
    if (busy || !userId || !status || status === currentStatus) return
    setBusy(true)

    try {
      let isOwner = false
      if (status !== 'active') {
        const ownerCheck = await dispatch(checkIsAffiliateContactGroupOwner(userId))
        // The owner warning is best-effort: on check failure proceed without it —
        // the confirm modal still warns about access loss.
        isOwner = !ownerCheck.error && Boolean(ownerCheck.payload?.data)
      }

      setState({ pending: true, isOwner, targetStatus: status, targetUserId: userId })
    } catch (error) {
      // An unexpected throw must not leave the controls permanently disabled.
      setState(IDLE)
      setBusy(false)
      showSnackBar("error", "Failed to change affiliate status")
    }
  }

  const confirm = async () => {
    if (!state.pending || inFlightRef.current) return
    const { targetUserId, targetStatus } = state
    inFlightRef.current = true
    setState(IDLE)

    try {
      const response = await dispatch(changeAffiliateStatus({ userId: targetUserId, status: targetStatus }))
      if (response.error) {
        showSnackBar("error", response.payload)
      } else {
        showSnackBar("success", `Affiliate status changed to ${targetStatus}`)
        clearListCache('affiliates')
        onSuccess?.(response.payload?.data)
      }
    } catch (error) {
      showSnackBar("error", "Failed to change affiliate status")
    } finally {
      inFlightRef.current = false
      setBusy(false)
    }
  }

  const cancel = () => {
    if (inFlightRef.current) return
    setState(IDLE)
    setBusy(false)
  }

  return {
    requestChange,
    confirm,
    cancel,
    pending: state.pending,
    isOwner: state.isOwner,
    targetStatus: state.targetStatus,
    targetUserId: state.targetUserId,
    busy,
  }
}

export default useAffiliateStatusChange
